import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { conciergeService } from '../../_services/concierge.service';
import { agentService } from '../../_services/agent.service';
import { AGENT_VISIBLE } from '../../config/featureFlags';
import BaileyAvatar, { BaileyStill } from '../agent/bailey/BaileyAvatar';
import { resolveAppearance } from '../agent/bailey/lookStore';
import { normaliseAppearance } from '../agent/bailey/appearance';
import { when, dayGroup } from '../agent/bailey/format';
import { Icon } from '../agent/BaileyBits';
import NotificationBell from '../notifications/NotificationBell';
import LocationPicker from '../multiLocation/LocationPicker';
import { hapticTick } from '../../_utils/haptics';
import { TONES, EXAMPLES, EXAMPLE_ASK, splitGreeting, joinGreeting } from './aiAssistantTone';
import { FILTERS, useConversation, useInbox, whoOf, callLength, prettyPhone } from '../receptionist/inboxData';
import ReceptionThread from '../receptionist/ReceptionThread';
import '../agent/agent.css';
import '../agent/bailey-tab.css';
import '../receptionist/receptionist.css';
import './AiAssistant.css';
import ReceptionistAllowance from '../pricing/ReceptionistAllowance';
import { alertReasonText, alertTitle, isSafeTransferNumber, transferErrorOf, resumeErrorText, TRANSFER_RULE } from './aiAssistantGuard';

/**
 * Settings → Bailey, your receptionist. One place, laid out like the Bailey
 * tab (/assistant): down the left, the parts of Bailey to set up and then
 * every conversation Bailey has had with clients (search, filters, paged);
 * on the right, one of those at a time: a setting, or the conversation as a
 * chat, with a box to step in.
 *
 * Bailey answers the booking page chat and, with a number of its own, the
 * salon's phone. Deliberately OFF by default; always says it's an AI. The look
 * is the owner's own Bailey (agent status), snapshotted onto these settings on
 * save so the booking page can draw Bailey's face.
 */

const SECTIONS = [
    { id: 'bailey', label: 'Bailey', icon: 'face' },
    { id: 'voice', label: 'Voice', icon: 'voice', sub: 'How Bailey sounds on the phone' },
    { id: 'talk', label: 'How Bailey talks', icon: 'talk', sub: 'Bailey’s manner, on the chat and the phone' },
    { id: 'phone', label: 'Phone number', icon: 'phone', sub: 'The number Bailey answers' },
    { id: 'knows', label: 'What Bailey knows', icon: 'book', sub: 'Answers only your salon knows' },
    { id: 'messages', label: 'Messages taken', icon: 'inbox', sub: 'When a client wants a person' }
];
// Questions every salon gets that Beu can't answer from its records.
const SUGGESTED_QUESTIONS = [
    'Where can clients park?',
    'Is the salon wheelchair accessible?',
    'Can I bring my children?',
    'How can I pay?',
    'What happens if I’m running late?',
    'Do you do patch tests?',
    'Is there Wi-Fi?',
    'Are dogs welcome?'
];
const GROUP_ORDER = ['Today', 'Yesterday', 'This week', 'This month', 'Earlier'];
const WIDE = 900;

const I = {
    voice: <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M4 8v4M7 5.5v9M10 3.5v13M13 6.5v7M16 8.5v3" /></svg>,
    talk: <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"><path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h9A1.5 1.5 0 0 1 16 5.5v6a1.5 1.5 0 0 1-1.5 1.5H9l-3.5 3v-3A1.5 1.5 0 0 1 4 11.5z" /></svg>,
    phone: <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"><path d="M6.2 3.5h-1.7A1.3 1.3 0 0 0 3.2 5c.4 6.2 5.6 11.4 11.8 11.8a1.3 1.3 0 0 0 1.5-1.3v-1.7a1 1 0 0 0-.8-1l-2.6-.6a1 1 0 0 0-1 .3l-.9 1a9 9 0 0 1-4.7-4.7l1-.9a1 1 0 0 0 .3-1L7.2 4.3a1 1 0 0 0-1-.8z" /></svg>,
    book: <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"><path d="M4 4.5A1.5 1.5 0 0 1 5.5 3H15v12.5H5.5A1.5 1.5 0 0 0 4 17zM4 17V4.5M7.5 7h4.5" /></svg>,
    inbox: <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"><path d="M3.5 11.5l2-6.5h9l2 6.5V15a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1zM3.5 11.5h4l1 2h3l1-2h4" /></svg>
};

// +441372233562 is not how anyone in Britain reads a phone number.
const prettyNumber = (e164) => prettyPhone(e164);

// House responseHandler: 200 with statusCode 0 when something went wrong.
const unwrap = (res) => {
    const body = (res && res.data) || {};
    if (body.statusCode === 0) {
        const err = new Error((body.error && body.error.responseMessage) || body.message || 'That did not work');
        err.payload = body;
        throw err;
    }
    return (body.responseData !== undefined ? body.responseData : body.data) || body;
};

const Toggle = ({ on, onChange, label, disabled }) => (
    <button type="button" role="switch" aria-checked={!!on} aria-label={label} disabled={disabled}
        className={`ds-toggle${on ? ' ds-toggle--on' : ''}`} onClick={() => !disabled && onChange(!on)}>
        <span className="ds-toggle-knob" />
    </button>
);
const PlayIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.4-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5Z" fill="currentColor" /></svg>;
const Bars = () => <span className="bra-bars" aria-hidden="true"><i /><i /><i /><i /></span>;
const Tick = () => <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.2 4.2L19 7" stroke="currentColor" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>;
const Face = ({ look, size = 18 }) => <span className="bra-face" style={{ width: size, height: size }} aria-hidden="true"><BaileyStill appearance={look || undefined} face /></span>;

// Two letters for a client's badge; a caller gets the phone icon instead.
const initialsOf = (row) => {
    const n = String((row && row.clientName) || '').trim();
    if (!n || /^\+?\d/.test(n)) return '?';
    return n.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
};

const EMPTY = { enabled: false, assistantName: 'Bailey', greeting: '', openingLine: '', voiceEnabled: false, voiceNumber: '', voiceTransferNumber: '', voiceProvider: '', voiceId: '' };
const EDITABLE = ['enabled', 'assistantName', 'greeting', 'openingLine', 'voiceEnabled', 'voiceTransferNumber', 'voiceProvider', 'voiceId'];

const params = () => { try { return new URLSearchParams(window.location.search); } catch (e) { return new URLSearchParams(); } };
const fromUrl = () => {
    const p = params();
    if (p.get('c')) return { kind: 'convo', id: p.get('c') };
    const s = p.get('s');
    return { kind: 'section', id: SECTIONS.some((x) => x.id === s) ? s : 'bailey' };
};

const AiAssistant = (props) => {
    const [settings, setSettings] = useState(EMPTY);
    const [saved, setSaved] = useState(EMPTY);
    const [look, setLook] = useState(null);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null);
    const [saving, setSaving] = useState(false);
    const [renaming, setRenaming] = useState(false);

    const [voices, setVoices] = useState(null);
    const [defaultVoiceId, setDefaultVoiceId] = useState('');
    const [playing, setPlaying] = useState(null);
    const [fetchingVoice, setFetchingVoice] = useState(null);
    const [samplesOk, setSamplesOk] = useState(true);
    const [sampleError, setSampleError] = useState('');
    const audio = useRef(null);
    const sampleUrls = useRef({});
    const answerRef = useRef(null);

    const [numberOptions, setNumberOptions] = useState(null);
    const [numberBusy, setNumberBusy] = useState(false);
    const [areaCode, setAreaCode] = useState('');
    const [numberError, setNumberError] = useState('');
    const [taking, setTaking] = useState('');

    // The anomaly brake: { paused, pausedAt, reason, alerts } (GET /alerts).
    const [brake, setBrake] = useState(null);
    const [resuming, setResuming] = useState(false);
    const [resumeError, setResumeError] = useState('');
    // The API's refusal of the put-through number (not UK-safe / owner only).
    const [transferError, setTransferError] = useState('');

    const [faqs, setFaqs] = useState([]);
    const [newFaq, setNewFaq] = useState({ question: '', answer: '' });
    const [messages, setMessages] = useState(null);

    // What the right-hand side shows, and (on a phone) whether it's showing.
    const [view, setView] = useState(fromUrl);
    const [vw, setVw] = useState(() => window.innerWidth);
    const [mobileMain, setMobileMain] = useState(() => !!(params().get('c') || params().get('s')));
    const [filter, setFilter] = useState('all');
    const [q, setQ] = useState('');
    const inbox = useInbox({ filter, q });
    const convo = useConversation(view.kind === 'convo' ? view.id : null, { onChange: inbox.patchRow });

    const say = (text, tone = 'ok') => setStatus(text ? { text, tone } : null);

    useEffect(() => {
        const on = () => setVw(window.innerWidth);
        window.addEventListener('resize', on);
        return () => window.removeEventListener('resize', on);
    }, []);

    const load = useCallback(async () => {
        try {
            const [s, f] = await Promise.all([conciergeService.getSettings(), conciergeService.getFaqs()]);
            const sd = unwrap(s), fd = unwrap(f);
            if (sd && sd.settings) {
                const next = {
                    enabled: !!sd.settings.enabled,
                    assistantName: sd.settings.assistantName || 'Bailey',
                    greeting: sd.settings.greeting || '',
                    openingLine: sd.settings.openingLine || '',
                    voiceEnabled: !!sd.settings.voiceEnabled,
                    voiceNumber: sd.settings.voiceNumber || '',
                    voiceTransferNumber: sd.settings.voiceTransferNumber || '',
                    voiceProvider: sd.settings.voiceProvider || '',
                    voiceId: sd.settings.voiceId || ''
                };
                setSettings(next);
                setSaved(next);
                if (sd.settings.avatar) setLook((l) => l || normaliseAppearance(sd.settings.avatar));
            }
            if (fd && fd.faqs) setFaqs(fd.faqs);
        } catch (e) { say('Couldn’t load Bailey’s settings. Refresh to try again.', 'warn'); }
        setLoading(false);
    }, []);
    useEffect(() => { load(); }, [load]);

    // Is the receptionist paused by the anomaly brake, and why. Quiet on
    // failure: the rest of the screen works without it.
    const loadBrake = useCallback(async () => {
        try {
            const r = unwrap(await conciergeService.getAlerts());
            setBrake({ paused: !!r.paused, pausedAt: r.pausedAt || null, reason: r.reason || '', alerts: Array.isArray(r.alerts) ? r.alerts : [] });
        } catch (e) { setBrake((b) => b || { paused: false, pausedAt: null, reason: '', alerts: [] }); }
    }, []);
    useEffect(() => { loadBrake(); }, [loadBrake]);

    const resumeBailey = async () => {
        setResuming(true);
        setResumeError('');
        try {
            unwrap(await conciergeService.resume());
            say(`${(settings.assistantName || '').trim() || 'Bailey'} is answering again.`);
            await loadBrake();
        } catch (e) { setResumeError(resumeErrorText(e.message)); }
        setResuming(false);
    };

    // The owner's own Bailey, as dressed in the Bailey tab.
    useEffect(() => {
        let alive = true;
        if (!AGENT_VISIBLE) return undefined;
        agentService.status().then((res) => {
            if (!alive) return;
            const chosen = resolveAppearance((res.data && res.data.responseData) || {});
            if (chosen) setLook(chosen);
        }).catch(() => {});
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        let alive = true;
        conciergeService.getVoices().then((res) => {
            if (!alive) return;
            const r = unwrap(res);
            setVoices(r.voices || []);
            setDefaultVoiceId(r.defaultVoiceId || '');
            setSamplesOk(r.samples !== false);
        }).catch(() => { if (alive) setVoices([]); });
        return () => { alive = false; };
    }, []);
    useEffect(() => () => {
        if (audio.current) audio.current.pause();
        Object.values(sampleUrls.current).forEach((u) => { try { URL.revokeObjectURL(u); } catch (e) { /* fine */ } });
    }, []);

    useEffect(() => {
        if (view.kind !== 'section' || view.id !== 'messages' || messages !== null) return;
        conciergeService.getMessages().then((r) => setMessages(unwrap(r).messages || [])).catch(() => setMessages([]));
    }, [view, messages]);

    const go = (next) => {
        hapticTick();
        setView(next);
        setMobileMain(true);
        setStatus(null);
        try {
            const qs = next.kind === 'convo' ? `?c=${next.id}` : next.id === 'bailey' ? '' : `?s=${next.id}`;
            window.history.replaceState(null, '', `${window.location.pathname}${qs}`);
        } catch (e) { /* fine */ }
    };

    const dirty = EDITABLE.some((k) => settings[k] !== saved[k]);
    const set = (patch) => { setSettings((p) => ({ ...p, ...patch })); setStatus(null); };
    const name = (settings.assistantName || '').trim() || 'Bailey';
    const { tone, notes } = splitGreeting(settings.greeting);
    const chosenVoiceId = settings.voiceId || defaultVoiceId;
    const chosenVoice = (voices || []).find((v) => v.id === chosenVoiceId);
    const live = settings.enabled || (settings.voiceEnabled && settings.voiceNumber);
    const brakePaused = !!(brake && brake.paused);
    const stateLabel = brakePaused && live ? 'Paused' : live ? 'Answering for you' : 'Switched off';
    const transferLooksWrong = !isSafeTransferNumber(settings.voiceTransferNumber);
    const speaking = !!playing;
    const wide = vw >= WIDE;
    const waiting = inbox.rows.filter((r) => r.needsReply).length;

    // Press play: the voice says hello as this salon's receptionist (made on
    // the server, then kept for the visit). The stock clip is the fallback.
    const preview = async (v) => {
        if (audio.current) { audio.current.pause(); audio.current = null; }
        setSampleError('');
        if (playing === v.id) { setPlaying(null); return; }
        let src = sampleUrls.current[v.id] || null;
        if (!src && samplesOk) {
            setFetchingVoice(v.id);
            try {
                const blob = await conciergeService.voiceSample(v.id);
                src = URL.createObjectURL(blob);
                sampleUrls.current[v.id] = src;
            } catch (e) {
                // The account can't make samples: say so once, keep choosing.
                if (/still choose a voice/.test(e.message || '')) setSamplesOk(false);
                else if (!v.previewUrl) setSampleError(e.message || `Couldn’t play ${v.name} just now.`);
            }
            setFetchingVoice(null);
        }
        src = src || v.previewUrl;
        if (!src) return;
        try {
            const a = new Audio(src);
            audio.current = a;
            setPlaying(v.id);
            a.onended = () => setPlaying(null);
            a.onerror = () => setPlaying(null);
            a.play().catch(() => setPlaying(null));
        } catch (e) { setPlaying(null); }
    };

    const save = async () => {
        setSaving(true);
        setTransferError('');
        try {
            const body = { ...settings, assistantName: name };
            if (look) body.avatar = look;
            await conciergeService.saveSettings(body).then(unwrap);
            setSettings(body);
            setSaved(body);
            say('Saved. Bailey’s up to date.');
        } catch (e) {
            // The put-through number refused (not a UK landline/mobile, or
            // not the owner): say it by the field, and take them there.
            const te = transferErrorOf(e.payload);
            if (te) {
                setTransferError(te);
                if (!(view.kind === 'section' && view.id === 'phone')) go({ kind: 'section', id: 'phone' });
                // go() clears the status line, so set it after.
                setTimeout(() => say(te, 'warn'), 0);
            } else {
                say(e.message || 'Couldn’t save just now. Try again.', 'warn');
            }
        }
        setSaving(false);
    };

    // --- the salon's own number: Beu buys it; never typed (it routes calls) ---
    const findNumbers = async () => {
        setNumberBusy(true);
        setNumberError('');
        try {
            const res = await conciergeService.getNumberOptions({ country: 'GB', areaCode: areaCode || undefined });
            setNumberOptions(unwrap(res).options || []);
        } catch (e) { setNumberError(e.message || 'Couldn’t look for numbers just now.'); }
        setNumberBusy(false);
    };
    const takeNumber = async (phoneNumber) => {
        setTaking(phoneNumber);
        setNumberError('');
        try {
            const r = unwrap(await conciergeService.provisionNumber({ country: 'GB', phoneNumber }));
            setSettings((p) => ({ ...p, voiceNumber: r.number }));
            setSaved((p) => ({ ...p, voiceNumber: r.number }));
            setNumberOptions(null);
            say(`${prettyNumber(r.number)} is yours. Switch on the phone and save when you’re ready.`);
        } catch (e) { setNumberError(e.message || 'That number couldn’t be taken.'); }
        setTaking('');
    };
    const giveNumberBack = async () => {
        // eslint-disable-next-line no-alert
        if (!window.confirm('Give this number back? Clients calling it will no longer reach Bailey, and you can’t get the same number again.')) return;
        setNumberBusy(true);
        try {
            unwrap(await conciergeService.releaseNumber());
            setSettings((p) => ({ ...p, voiceNumber: '', voiceEnabled: false }));
            setSaved((p) => ({ ...p, voiceNumber: '', voiceEnabled: false }));
            say('Number given back.');
        } catch (e) { say(e.message || 'Couldn’t give the number back.', 'warn'); }
        setNumberBusy(false);
    };

    const addFaq = async () => {
        if (!newFaq.question.trim() || !newFaq.answer.trim()) return;
        try {
            unwrap(await conciergeService.saveFaq(newFaq));
            setNewFaq({ question: '', answer: '' });
            const f = unwrap(await conciergeService.getFaqs());
            if (f && f.faqs) setFaqs(f.faqs);
        } catch (e) { say(e.message || 'Couldn’t save that answer.', 'warn'); }
    };
    const removeFaq = async (faqId) => {
        try {
            unwrap(await conciergeService.deleteFaq(faqId));
            setFaqs((rows) => rows.filter((r) => r._id !== faqId));
        } catch (e) { say(e.message || 'Couldn’t remove that answer.', 'warn'); }
    };

    const groups = useMemo(() => {
        const by = {};
        inbox.rows.forEach((r) => { const g = dayGroup(r.lastActivityAt); (by[g] = by[g] || []).push(r); });
        return GROUP_ORDER.filter((g) => by[g]).map((g) => ({ name: g, rows: by[g] }));
    }, [inbox.rows]);
    const firstWaiting = inbox.rows.find((r) => r.needsReply);

    // ─── The sections ───
    const sectionBailey = (
        <>
            {brakePaused && (
                <section className="bra-paused" role="alert">
                    <div>
                        <b>{name} has paused itself</b>
                        <p>
                            {alertReasonText(brake.reason)} To be safe, {name} stopped answering{brake.pausedAt ? ` at ${new Date(brake.pausedAt).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}` : ''}.
                            Clients are given your booking link and phone number instead. {name} stays paused until you resume it.
                        </p>
                        {resumeError && <p className="bra-paused-error">{resumeError}</p>}
                    </div>
                    <button type="button" className="bra-btn" disabled={resuming} onClick={resumeBailey}>{resuming ? 'Resuming…' : `Resume ${name}`}</button>
                </section>
            )}
            <section className="bra-hero">
                <div className={`bra-stage${speaking ? ' is-speaking' : ''}`}>
                    {AGENT_VISIBLE
                        ? <BaileyAvatar appearance={look || undefined} state={speaking ? 'complete' : 'attentive'} className="bra-avatar" />
                        : <div className="bra-still"><BaileyStill appearance={look || undefined} /></div>}
                    {speaking && <span className="bra-speaking"><Bars /> Speaking</span>}
                </div>
                <div className="bra-hero-text">
                    <span className={`bra-state${brakePaused && live ? ' is-paused' : live ? ' is-live' : ''}`}><i />{stateLabel}</span>
                    {renaming ? (
                        <form className="bra-rename" onSubmit={(e) => { e.preventDefault(); setRenaming(false); }}>
                            <input autoFocus maxLength={60} value={settings.assistantName} aria-label="Receptionist’s name"
                                onChange={(e) => set({ assistantName: e.target.value })} onBlur={() => setRenaming(false)} />
                        </form>
                    ) : (
                        <h1 className="bra-name">{name}<button type="button" className="bra-link" onClick={() => setRenaming(true)}>Rename</button></h1>
                    )}
                    <p className="bra-lede">
                        Answers your booking page chat and your phone, day and night. Only your real prices and genuinely free times, never card details, and always says it’s an AI.
                    </p>
                    <div className="bra-channels">
                        <div className="bra-channel">
                            <div><b>Chat on your booking page</b><span>Clients ask, book and leave messages.</span></div>
                            <Toggle label="Chat on your booking page" on={settings.enabled} onChange={(v) => set({ enabled: v })} />
                        </div>
                        <div className="bra-channel">
                            <div>
                                <b>Your phone</b>
                                <span>{settings.voiceNumber ? `Answers calls to ${prettyNumber(settings.voiceNumber)}.` : <>Needs a number first. <button type="button" className="bra-link" onClick={() => go({ kind: 'section', id: 'phone' })}>Get one</button></>}</span>
                            </div>
                            <Toggle label="Answer my phone" on={settings.voiceEnabled} disabled={!settings.voiceNumber} onChange={(v) => set({ voiceEnabled: v })} />
                        </div>
                    </div>
                    {AGENT_VISIBLE && (
                        <button type="button" className="bra-link bra-look" onClick={() => props.history && props.history.push('/assistant')}>Change how Bailey looks</button>
                    )}
                </div>
            </section>
            {/* Pricing (Oct 2026): this month's allowance, and the way to Plan & usage. */}
            <ReceptionistAllowance history={props.history} className="bra-allowance" />
            <div className="bra-glance">
                <button type="button" className="bra-glance-tile" onClick={() => (firstWaiting ? go({ kind: 'convo', id: firstWaiting._id }) : setFilter('needs'))}>
                    <span>Waiting for you</span><b className={waiting ? 'is-warn' : ''}>{waiting}</b>
                </button>
                <button type="button" className="bra-glance-tile" onClick={() => go({ kind: 'section', id: 'voice' })}>
                    <span>Voice</span><b>{chosenVoice ? chosenVoice.name : 'Default'}</b>
                </button>
                <button type="button" className="bra-glance-tile" onClick={() => go({ kind: 'section', id: 'talk' })}>
                    <span>Talks</span><b>{(TONES.find((t) => t.id === tone) || {}).label || 'Your way'}</b>
                </button>
                <button type="button" className="bra-glance-tile" onClick={() => go({ kind: 'section', id: 'knows' })}>
                    <span>Your answers</span><b>{faqs.length}</b>
                </button>
            </div>
            {brake && brake.alerts.length > 0 && (
                <section className="bra-card bra-alerts" aria-label="Alerts">
                    <div className="bra-card-head"><h2>Alerts</h2><p>When {name} sees something unusual it pauses itself and tells you here.</p></div>
                    {brake.alerts.map((a) => (
                        <div key={a._id || `${a.kind}-${a.createdAt}`} className="bra-row">
                            <b>{alertTitle(a)}{a.resolvedAt ? <em className="bra-alert-tag">Resumed</em> : a.kind === 'auto_pause' ? <em className="bra-alert-tag is-open">Paused</em> : null}</b>
                            <p>{alertReasonText(a.reason)}</p>
                            <small>
                                {a.createdAt ? new Date(a.createdAt).toLocaleString('en-GB') : ''}
                                {a.resolvedAt ? ` · resumed ${new Date(a.resolvedAt).toLocaleString('en-GB')}` : ''}
                            </small>
                        </div>
                    ))}
                </section>
            )}
        </>
    );

    const sectionVoice = (
        <section className="bra-card is-split">
            <div className={`bra-mini-stage${speaking ? ' is-speaking' : ''}`}>
                <div className="bra-mini-face"><BaileyStill appearance={look || undefined} face /></div>
                <b>{chosenVoice ? chosenVoice.name : 'Default voice'}</b>
                <span>{chosenVoice ? [chosenVoice.accent, chosenVoice.vibe].filter(Boolean).join(' · ') : 'Pick one to hear it'}</span>
                {speaking ? <em><Bars /> Playing</em> : fetchingVoice ? <em>Getting ready…</em> : null}
            </div>
            <div className="bra-split-main">
                <p className="bra-intro">Press play to hear each voice answer as your salon, then tap the one you want.</p>
                {voices === null ? (
                    <div className="bra-voices">{[0, 1, 2, 3].map((i) => <div key={i} className="bra-voice is-hold" aria-hidden />)}</div>
                ) : (
                    <>
                        <div className="bra-voices" role="radiogroup" aria-label={`${name}’s voice`}>
                            {voices.map((v) => {
                                const chosen = chosenVoiceId === v.id;
                                const isPlaying = playing === v.id;
                                return (
                                    <div key={v.id} className={`bra-voice${chosen ? ' is-chosen' : ''}${isPlaying ? ' is-playing' : ''}`}>
                                        <button type="button" className={`bra-play${fetchingVoice === v.id ? ' is-loading' : ''}`} onClick={() => preview(v)} disabled={(!samplesOk && !v.previewUrl) || !!fetchingVoice}
                                            aria-label={isPlaying ? `Stop ${v.name}` : `Hear ${v.name}`}>
                                            {isPlaying ? <Bars /> : fetchingVoice === v.id ? <span className="bra-spin" aria-hidden="true" /> : <PlayIcon />}
                                        </button>
                                        <button type="button" role="radio" aria-checked={chosen} className="bra-voice-pick" onClick={() => set({ voiceProvider: v.provider, voiceId: v.id })}>
                                            <span className="bra-voice-name">{v.name}{v.id === defaultVoiceId && !settings.voiceId ? <em>Default</em> : null}</span>
                                            <span className="bra-voice-meta">{[v.accent, v.vibe].filter(Boolean).join(' · ')}</span>
                                        </button>
                                        {chosen && <span className="bra-voice-tick"><Tick /></span>}
                                    </div>
                                );
                            })}
                        </div>
                        {settings.voiceId && !voices.some((v) => v.id === settings.voiceId) && <p className="bra-note">You’re using a voice that isn’t on this list. Pick one above to switch.</p>}
                        {sampleError && <p className="bra-note is-warn" role="alert">{sampleError}</p>}
                        {!samplesOk && !voices.some((v) => v.previewUrl) && <p className="bra-note">Voice samples aren’t available right now, but you can still choose a voice.</p>}
                    </>
                )}
            </div>
        </section>
    );

    const sectionTalk = (
        <section className="bra-card">
            <p className="bra-intro">Pick a feel, then add anything that’s very you.</p>
            <div className="bra-tones" role="radiogroup" aria-label={`How ${name} talks`}>
                {TONES.map((t) => (
                    <button key={t.id} type="button" role="radio" aria-checked={tone === t.id} className={`bra-tone${tone === t.id ? ' is-on' : ''}`}
                        onClick={() => set({ greeting: joinGreeting(tone === t.id ? '' : t.id, notes) })}>
                        <b>{t.label}</b><span>{t.line}</span>
                    </button>
                ))}
            </div>
            <label className="bra-field">
                <span>First message clients see <em>Optional</em></span>
                <input type="text" maxLength={300} value={settings.openingLine || ''}
                    placeholder={`Hi, I’m ${name}! I can check prices, find you an appointment, or pass a message to the team. What do you need?`}
                    onChange={(e) => set({ openingLine: e.target.value })} />
            </label>
            <label className="bra-field">
                <span>Anything else? <em>Optional</em></span>
                <textarea rows={3} maxLength={440} value={notes} placeholder="e.g. Use first names, mention we’re dog-friendly, suggest a treatment with a cut"
                    onChange={(e) => set({ greeting: joinGreeting(tone, e.target.value) })} />
            </label>
            <div className="bra-sounds">
                <span className="bra-sounds-label">How that sounds</span>
                <div className="bra-sounds-chat">
                    <div className="bra-say is-client">{EXAMPLE_ASK}</div>
                    <div className="bra-say is-bailey" key={tone}><Face look={look} size={20} /><span>{EXAMPLES[tone] || EXAMPLES['']}</span></div>
                </div>
                <small>An example. {name} always uses your real services, prices and free times.</small>
            </div>
        </section>
    );

    const sectionPhone = (
        <section className="bra-card">
            <p className="bra-intro">A local number of your own for {name} to answer. Callers pay standard rates; it isn’t a premium line.</p>
            {numberError && <div className="bra-status is-warn">{numberError}</div>}
            {settings.voiceNumber ? (
                <>
                    <div className="bra-number">
                        <div>
                            <span className="bra-number-digits">{prettyNumber(settings.voiceNumber)}</span>
                            <small>Ring it from your mobile to hear {name} answer.</small>
                        </div>
                        <div className="bra-number-side">
                            <div className="bra-number-toggle"><span>{settings.voiceEnabled ? `${name} answers` : 'Switched off'}</span><Toggle label="Answer this number" on={settings.voiceEnabled} onChange={(v) => set({ voiceEnabled: v })} /></div>
                            <button type="button" className="bra-link is-danger" disabled={numberBusy} onClick={giveNumberBack}>Give it back</button>
                        </div>
                    </div>
                    <details className="bra-how">
                        <summary>Keep your own number and send calls to {name}</summary>
                        <ol>
                            <li>Ask your phone provider to divert calls to <b>{prettyNumber(settings.voiceNumber)}</b> when nobody answers, or when you’re closed.</li>
                            <li>On BT and most UK landlines you can do it yourself: dial <b>*61*{String(prettyNumber(settings.voiceNumber)).replace(/\s/g, '')}#</b> to divert when there’s no answer. Dial <b>#61#</b> to stop.</li>
                            <li>Ring your salon number and let it go unanswered to check {name} picks up.</li>
                        </ol>
                    </details>
                </>
            ) : numberOptions ? (
                <>
                    <p className="bra-note">
                        {numberOptions.length
                            ? 'Whichever you choose is yours straight away. You can give it back later, but you can’t get the same one again, so pick one you’d put on your shop window.'
                            : areaCode ? `Nothing free on ${areaCode} right now. Try a nearby area code, or clear it to see what’s available.` : 'No numbers free right now. Try again shortly.'}
                    </p>
                    <div className="bra-options">
                        {numberOptions.map((n) => (
                            <div key={n.number} className="bra-option">
                                <span>{prettyNumber(n.number)}</span>
                                <button type="button" className="bra-btn" disabled={!!taking} onClick={() => takeNumber(n.number)}>{taking === n.number ? 'Getting it…' : 'Choose'}</button>
                            </div>
                        ))}
                    </div>
                    <button type="button" className="bra-link" onClick={() => { setNumberOptions(null); setNumberError(''); }}>Look somewhere else</button>
                </>
            ) : (
                <div className="bra-find">
                    <input value={areaCode} placeholder="Area code, e.g. 0161" aria-label="Area code" onChange={(e) => setAreaCode(e.target.value)} />
                    <button type="button" className="bra-btn" disabled={numberBusy} onClick={findNumbers}>{numberBusy ? 'Finding numbers…' : 'Get my number'}</button>
                </div>
            )}
            <label className="bra-field">
                <span>When a caller asks for a person <em>Optional</em></span>
                <input value={settings.voiceTransferNumber} placeholder="Your salon phone" inputMode="tel"
                    aria-invalid={transferLooksWrong || !!transferError}
                    onChange={(e) => { setTransferError(''); set({ voiceTransferNumber: e.target.value }); }} />
                <small>{name} puts them through to this number. Leave it empty and {name} takes a message instead. UK landline (01, 02, 03) or mobile only. Only the salon owner can change it.</small>
                {(transferError || transferLooksWrong) && <small className="bra-field-error" role="alert">{transferError || TRANSFER_RULE}</small>}
            </label>
        </section>
    );

    const sectionKnows = (
        <section className="bra-card">
            <p className="bra-intro">Prices, hours, services and free times come from Beu automatically. Add only what Beu can’t know.</p>
            {(() => {
                const asked = new Set(faqs.map((f) => String(f.question || '').trim().toLowerCase()));
                const left = SUGGESTED_QUESTIONS.filter((x) => !asked.has(x.toLowerCase()));
                return left.length ? (
                    <div className="bra-suggest" aria-label="Common questions">
                        {left.map((x) => (
                            <button key={x} type="button" className={`bra-suggest-chip${newFaq.question === x ? ' is-on' : ''}`}
                                onClick={() => { setNewFaq({ question: x, answer: newFaq.question === x ? newFaq.answer : '' }); setTimeout(() => answerRef.current && answerRef.current.focus(), 0); }}>
                                + {x}
                            </button>
                        ))}
                    </div>
                ) : null;
            })()}
            <div className="bra-faq-new">
                <input placeholder="Or write your own question" value={newFaq.question} onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })} />
                <textarea ref={answerRef} rows={2} placeholder={`${name}’s answer`} value={newFaq.answer} onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })} />
                <button type="button" className="bra-btn" disabled={!newFaq.question.trim() || !newFaq.answer.trim()} onClick={addFaq}>Add answer</button>
            </div>
            {faqs.length > 0 && (
                <div className="bra-faqs">
                    {faqs.map((f) => (
                        <div key={f._id} className="bra-faq">
                            <div><b>{f.question}</b><p>{f.answer}</p></div>
                            <button type="button" className="bra-link is-danger" onClick={() => removeFaq(f._id)}>Remove</button>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );

    const sectionMessages = (
        <section className="bra-card">
            <p className="bra-intro">When a client wants a person, {name} takes their name, how to reach them and what it’s about.</p>
            {messages === null ? <p className="bra-note">Loading…</p>
                : messages.length === 0 ? <div className="bra-empty is-face"><Face look={look} size={40} /><span>Nothing waiting. When {name} takes a message for you, it lands here.</span></div>
                    : messages.map((m) => (
                        <div key={m._id} className="bra-row">
                            <b>{m.clientName || 'A client'}{m.clientContact ? ` · ${m.clientContact}` : ''}</b>
                            <p>{m.message}</p>
                            <small>{m.createdAt ? new Date(m.createdAt).toLocaleString('en-GB') : ''}</small>
                        </div>
                    ))}
        </section>
    );

    const sectionBody = { bailey: sectionBailey, voice: sectionVoice, talk: sectionTalk, phone: sectionPhone, knows: sectionKnows, messages: sectionMessages };
    const sectionMeta = SECTIONS.find((s) => view.kind === 'section' && s.id === view.id);
    const current = view.kind === 'convo' ? (convo.convo || inbox.rows.find((r) => String(r._id) === String(view.id)) || null) : null;
    const labelOf = (s) => (s.id === 'bailey' ? name : s.label.replace('Bailey', name));

    // ─── Left: set-up, then conversations (the /assistant sidebar) ───
    const showSide = wide || !mobileMain;
    const sidebar = (
        <div className="bly-dock-side rcp-side">
            <aside className="bly-side" aria-label={name}>
                <div className="bly-side-top">
                    <button type="button" className="bly-iconbtn" onClick={() => props.history && props.history.push('/settings')} aria-label="All settings" title="All settings">
                        <Icon.Back size={19} />
                    </button>
                    <span className="bra-side-title">AI receptionist</span>
                </div>
                <nav className="bly-side-actions bra-nav" aria-label="Set up">
                    {SECTIONS.map((s) => {
                        const on = view.kind === 'section' && view.id === s.id;
                        return (
                            <button key={s.id} type="button" className={`bly-side-home${on ? ' is-active' : ''}`} aria-current={on ? 'page' : undefined} onClick={() => go({ kind: 'section', id: s.id })}>
                                {s.icon === 'face' ? <Face look={look} size={20} /> : I[s.icon]}
                                <span>{labelOf(s)}</span>
                                {s.id === 'bailey' && <i className={`bra-live-dot${brakePaused && live ? ' is-paused' : live ? ' is-live' : ''}`} aria-label={brakePaused && live ? 'Paused' : live ? 'On' : 'Off'} />}
                            </button>
                        );
                    })}
                </nav>
                <div className="bly-side-actions rcp-side-find">
                    <label className="bly-search">
                        <Icon.Search />
                        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a client or a message" aria-label="Find a conversation" />
                    </label>
                    <div className="rcp-filters is-strip" role="tablist" aria-label="Show">
                        {FILTERS.map((f) => (
                            <button key={f.id} type="button" role="tab" aria-selected={filter === f.id} className={`rcp-filter${filter === f.id ? ' is-on' : ''}`} onClick={() => { hapticTick(); setFilter(f.id); }}>
                                {f.label}{f.id === 'needs' && waiting > 0 && filter !== 'needs' ? <i>{waiting}</i> : null}
                            </button>
                        ))}
                    </div>
                </div>
                <h3 className="bly-side-label">Conversations</h3>
                <div className="bly-side-scroll">
                    {!inbox.loaded && <div className="bly-tl-note">Loading…</div>}
                    {inbox.error && <div className="bly-tl-note is-bad" role="alert">{inbox.error}</div>}
                    {inbox.loaded && !inbox.rows.length && !inbox.error && (
                        <div className="bly-tl-note">{q || filter !== 'all' ? 'Nothing matches that.' : `No conversations yet. When clients chat with ${name} or call, they land here.`}</div>
                    )}
                    {groups.map((g) => (
                        <section key={g.name} className="bly-tl-group">
                            <h4>{g.name}</h4>
                            {g.rows.map((r) => {
                                const active = view.kind === 'convo' && String(r._id) === String(view.id);
                                return (
                                    <div key={r._id} className={`bly-tl-row rcp-row${active ? ' is-active' : ''}${r.needsReply ? ' is-waiting' : ''}`}>
                                        <button type="button" className="bly-tl-open" onClick={() => go({ kind: 'convo', id: r._id })} aria-current={active ? 'true' : undefined}>
                                            <span className="rcp-row-top"><span className="bly-tl-title">{whoOf(r)}</span><small>{when(r.lastActivityAt)}</small></span>
                                            <span className="rcp-row-preview">{r.lastFrom === 'staff' ? 'You: ' : r.lastFrom === 'assistant' ? `${name}: ` : ''}{r.preview || (r.channel === 'voice' ? 'Phone call' : 'No messages')}</span>
                                            <span className="rcp-tags">
                                                {r.needsReply && <em className="is-needs">Needs you</em>}
                                                {r.takeover && !r.needsReply && <em className="is-held">With you</em>}
                                                {r.booked && <em className="is-booked">Booked</em>}
                                                {r.channel === 'voice' && <em>Phone</em>}
                                                {r.guest && r.channel !== 'voice' && <em>Guest</em>}
                                            </span>
                                        </button>
                                    </div>
                                );
                            })}
                        </section>
                    ))}
                    {inbox.more && (
                        <button type="button" className="bly-tl-more" onClick={inbox.loadOlder} disabled={inbox.busyMore}>{inbox.busyMore ? 'Loading…' : 'Show older conversations'}</button>
                    )}
                </div>
            </aside>
        </div>
    );

    const header = (
        <header className="bly-head">
            {!wide && (
                <button type="button" className="bly-app-menu is-back" onClick={() => { hapticTick(); setMobileMain(false); }} aria-label="Back">
                    <Icon.Back size={22} />
                </button>
            )}
            <div className="bly-head-who">
                {view.kind === 'convo' ? (
                    <>
                        <div className={`rcp-who-badge${current && current.channel === 'voice' ? ' is-call' : ''}`} aria-hidden="true">
                            {current && current.channel === 'voice' ? I.phone : initialsOf(current)}
                        </div>
                        <div className="bly-head-copy">
                            <b>{current ? whoOf(current) : 'Conversation'}</b>
                            <span className="rcp-head-sub">{current ? [current.channel === 'voice' ? `Phone call${current.seconds ? `, ${callLength(current.seconds)}` : ''}` : 'Booking page chat', current.guest ? 'Guest' : 'Client', current.booked ? 'Booked' : null, current.lastActivityAt ? when(current.lastActivityAt) : null].filter(Boolean).join(' · ') : ''}</span>
                        </div>
                    </>
                ) : (
                    <div className="bly-head-copy">
                        <b>{sectionMeta && sectionMeta.id !== 'bailey' ? labelOf(sectionMeta) : `${name}, your receptionist`}</b>
                        <span className="rcp-head-sub">{sectionMeta && sectionMeta.sub ? sectionMeta.sub.replace('Bailey', name) : stateLabel}</span>
                    </div>
                )}
            </div>
            <div className="bly-head-right">
                <LocationPicker /><span className="bly-bell"><NotificationBell /></span>
            </div>
        </header>
    );

    const main = (
        <div className="bly-main">
            {header}
            {view.kind === 'convo' ? (
                <ReceptionThread convo={convo.convo} error={convo.error} sending={convo.sending} onReply={convo.reply} onTakeover={convo.takeover} assistantName={name} look={look} />
            ) : (
                <div className="bly-scroll bra-pane">
                    <div className="bra">
                        {loading ? <div className="bra-loading">Getting Bailey ready…</div> : (
                            <>
                                {status && <div className={`bra-status is-${status.tone}`} role="status">{status.text}</div>}
                                {sectionBody[view.id] || sectionBailey}
                            </>
                        )}
                    </div>
                </div>
            )}
            {dirty && (
                <div className="bra-savebar" role="region" aria-label="Unsaved changes">
                    <span>You have changes that aren’t saved.</span>
                    <div>
                        <button type="button" className="bra-btn is-ghost" disabled={saving} onClick={() => { setSettings(saved); setStatus(null); }}>Undo</button>
                        <button type="button" className="bra-btn" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="body_container agt-page bly-page rcp-page bra-page">
            <div className="bly has-side" style={{ height: '100vh' }}>
                {showSide && sidebar}
                {(wide || mobileMain) && main}
            </div>
        </div>
    );
};

export default AiAssistant;
